/**
 * Public pricing-card "accepted payment methods" badge row (Tier 14).
 *
 * One-time fetch of `/api/billing/payment-methods` (cached in module
 * scope so all four pricing cards share the same network call) +
 * render a row of inline SVG badges per the active provider's
 * `acceptedMethods()` set. Free tier shows nothing.
 */

import { useEffect, useState, type JSX } from 'react';
import { PRICING } from '@rynora/messages';
import { getApiBaseUrl } from '../../../lib/config';
import { PaymentIcons } from './payment-icons';

type ProviderId = 'stripe' | 'razorpay' | 'paddle' | 'paypal' | 'lemonsqueezy' | 'manual';

interface PaymentMethodsResponse {
  provider: ProviderId;
  currency: string;
  acceptedMethods: string[];
  enabled: boolean;
}

let cachedResponse: PaymentMethodsResponse | null = null;
let inflight: Promise<PaymentMethodsResponse | null> | null = null;

async function loadOnce(): Promise<PaymentMethodsResponse | null> {
  if (cachedResponse) return cachedResponse;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/billing/payment-methods`);
      if (!res.ok) return null;
      const json = (await res.json()) as PaymentMethodsResponse;
      cachedResponse = json;
      return json;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export interface PaymentMethodsRowProps {
  /** Hide the row on the free tier. */
  hidden?: boolean;
}

export function PaymentMethodsRow({ hidden = false }: PaymentMethodsRowProps): JSX.Element | null {
  const [data, setData] = useState<PaymentMethodsResponse | null>(cachedResponse);

  useEffect(() => {
    if (cachedResponse) {
      setData(cachedResponse);
      return;
    }
    let mounted = true;
    void loadOnce().then((d) => {
      if (mounted) setData(d);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (hidden) return null;
  if (!data || data.acceptedMethods.length === 0) return null;
  if (data.enabled === false) return null;

  const providerLabel = PRICING.providerLabels[data.provider] ?? data.provider;

  return (
    <div
      data-testid="payment-methods-row"
      style={{
        marginTop: 16,
        paddingTop: 12,
        borderTop: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div
        title={PRICING.acceptedPaymentMethodsTooltip}
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {PRICING.acceptedPaymentMethodsLabel}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {data.acceptedMethods.map((m) => {
          const Icon = PaymentIcons[m];
          if (!Icon) return null;
          return <Icon key={m} />;
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{PRICING.poweredBy(providerLabel)}</div>
    </div>
  );
}
